/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Stack, Flash, Dialog} from '@primer/react'
import {StopIcon} from '@primer/octicons-react'
import {useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useConvertToDraftMutation} from '../../hooks/mutations/use-convert-to-draft-mutation'
import {useAnalytics} from '@github-ui/use-analytics'
import {useIsMergeBoxSidePanel} from '../../contexts/MergeBoxLayoutContext'

export function ConvertToDraftSection() {
  const isSidePanel = useIsMergeBoxSidePanel()
  const [dialogState, setDialogState] = useState<'closed' | 'open'>('closed')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {sendAnalyticsEvent} = useAnalytics()

  const onOpenConvertToDraft = () => {
    setDialogState('open')
    setErrorMessage(null)
  }

  const {mutate: convertToDraftMutation, isPending} = useConvertToDraftMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      setTimeout(() => announce('Failed to convert the pull request to draft'), 1000)
    },
  })

  const handleConvertToDraft = () => {
    if (isPending) return
    setErrorMessage(null)

    convertToDraftMutation(undefined, {
      onSuccess: () => {
        setDialogState('closed')
        setTimeout(() => announce('The pull request was successfully converted to draft.'), 1000)
        sendAnalyticsEvent(
          'convert_to_draft_section.convert_to_draft',
          'MERGEBOX_CONVERT_TO_DRAFT_SECTION_CONVERT_TO_DRAFT_BUTTON',
        )
      },
    })
  }

  const handleDismiss = () => {
    if (!isPending) setDialogState('closed')
  }

  return (
    <>
      <Stack
        direction="horizontal"
        align="baseline"
        className={`text-small ${isSidePanel ? 'flex-justify-start tmp-px-3 py-1' : 'flex-justify-end px-1 py-2'}`}
        gap="none"
      >
        <span className="fgColor-muted pr-1">Still in progress?</span>
        <Button variant="link" className="Link--muted Link--inTextBlock text-normal" onClick={onOpenConvertToDraft}>
          Convert to draft
        </Button>
      </Stack>

      {dialogState === 'open' && (
        <Dialog
          title="Convert this pull request to draft?"
          role="alertdialog"
          onClose={handleDismiss}
          footerButtons={[
            {content: 'Cancel', buttonType: 'default', onClick: handleDismiss, disabled: isPending},
            {
              content: 'Convert to draft',
              buttonType: 'default',
              onClick: handleConvertToDraft,
              loading: isPending,
              loadingAnnouncement: 'Converting to draft',
              autoFocus: true,
            },
          ]}
        >
          <Flash variant="warning">People who are already subscribed will not be unsubscribed.</Flash>

          {errorMessage && (
            <Flash className="my-2" variant="danger">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
        </Dialog>
      )}
    </>
  )
}
